import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { UserCircleIcon, XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const UserView = () => {
  // ===== HOOKS ET CONTEXTE =====
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user: currentUser } = useContext(AuthContext);
  
  // ===== ÉTATS =====
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    birthDate: '',
    birthPlace: '',
    socialSecurityNumber: '',
    showLeaveNumber: '',
    phone: '',
    iban: '',
    isPermanent: false,
    role: ''
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // ===== EFFETS =====
  // Chargement des données de l'utilisateur
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/users/${id}`);
        const user = response.data.data.user;
        setUserData({
          ...user,
          birthDate: user.birthDate && user.birthDate !== 'Invalid Date' && !isNaN(new Date(user.birthDate)) ? new Date(user.birthDate).toISOString().split('T')[0] : ''
        });
      } catch (err) {
        setError(err.response?.data?.message || 'Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, api]);

  // ===== FONCTIONS UTILITAIRES =====
  const getRoleName = (role) => {
    const roles = {
      'administrateur': 'Administrateur',
      'artistesProd': 'Artistes en production',
      'artistesCoProd': 'Artistes en Coproduction',
      'permanence': 'Permanence',
      'billeterie': 'Billetterie',
      'regie': 'Régie',
      'professeur': 'Professeur'
    };
    return roles[role] || role;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non renseigné';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  // ===== RENDU =====
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-t-gold">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto relative">
      {/* Image de théâtre floutée en arrière-plan de toute la page */}
      <div 
        className="fixed inset-0 bg-cover bg-center opacity-25 z-0" 
        style={{ 
          backgroundImage: 'url("/images/theatre-background.jpg")', 
          filter: 'blur(2px)',
          top: '64px' // Commence après la navbar
        }}
      ></div>
      
      {/* Contenu principal avec z-index pour passer au-dessus du background */}
      <div className="relative z-10 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6 border-t-4 border-t-gold">
          {/* En-tête */}
          <div className="flex items-center mb-8">
            <button
              onClick={() => navigate('/users')}
              className="mr-4 p-2 text-gray-600 hover:text-primary transition-colors duration-200"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <div className="flex items-center">
              <UserCircleIcon className="h-8 w-8 text-primary mr-3" />
              <h1 className="text-2xl font-bold text-primary">
                Profil de {userData.firstName} {userData.lastName}
              </h1>
            </div>
          </div>

      {/* Messages d'erreur */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded shadow-md">
          <div className="flex">
            <div className="py-1">
              <XMarkIcon className="h-6 w-6 text-red-500 mr-3" />
            </div>
            <div>
              <p className="font-bold">Erreur</p>
              <p>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Section Informations personnelles */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-primary mb-6 border-b border-gold pb-2">
            Informations personnelles
          </h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900">
                  {userData.firstName || 'Non renseigné'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900">
                  {userData.lastName || 'Non renseigné'}
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
              <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900">
                {getRoleName(userData.role)}
              </div>
            </div>
            
            <div className="flex items-center">
              <label className="block text-sm font-medium text-gray-700 mr-4">Statut permanent</label>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                userData.isPermanent 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-gray-100 text-gray-800 border border-gray-200'
              }`}>
                {userData.isPermanent ? 'Autorisé' : 'Non autorisé'}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900">
                  {formatDate(userData.birthDate)}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lieu de naissance</label>
                <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900">
                  {userData.birthPlace || 'Non renseigné'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Contact */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-primary mb-6 border-b border-gold pb-2">
            Informations de contact
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900">
                {userData.email ? (
                  <a href={`mailto:${userData.email}`} className="text-primary hover:text-accent hover:underline">
                    {userData.email}
                  </a>
                ) : 'Non renseigné'}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900">
                {userData.phone ? (
                  <a href={`tel:${userData.phone}`} className="text-primary hover:text-accent hover:underline">
                    {userData.phone}
                  </a>
                ) : 'Non renseigné'}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
              <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 min-h-[2.5rem]">
                {userData.address || 'Non renseignée'}
              </div>
            </div>
          </div>
        </div>

        {/* Section Informations professionnelles */}
        <div className="bg-gray-50 rounded-lg p-6 lg:col-span-2">
          <h2 className="text-xl font-semibold text-primary mb-6 border-b border-gold pb-2">
            Informations professionnelles
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de sécurité sociale</label>
              <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 font-mono">
                {userData.socialSecurityNumber || 'Non renseigné'}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de congé spectacle</label>
              <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 font-mono">
                {userData.showLeaveNumber || 'Non renseigné'}
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
              <div className="px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 font-mono">
                {userData.iban || 'Non renseigné'}
              </div>
            </div>
          </div>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default UserView;